import { useEffect, useState, useContext } from "react"
import Modal from "src/components/Common/Modal"
import { SCORE_KEYS_TYPES } from "./constants"
import { CloseIcon } from "src/assets/icons"
import { ReportPdfContext } from "src/utils/contexts"
import "./styles.scss"

const ScoreBreakdownModal = ({ breakdownObj, reportPdfView: reportPdfViewProp }) => {
    const { reportPdfView: reportPdfViewContext } = useContext(ReportPdfContext) || {}
    const reportPdfView = reportPdfViewProp ?? reportPdfViewContext

    const [showBreakdown, setShowBreakdown] = useState(false)
    const [segregatedBreakdown, setSegregatedBreakdown] = useState([])

    useEffect(() => {
        if (breakdownObj) {
            delete breakdownObj?.answer_relevance_details?.penalties
            delete breakdownObj?.dimension_scores
            for (const key of SCORE_KEYS_TYPES) {
                const keysArr = extractTypeFromBreakdown(key)
                setSegregatedBreakdown(prev => [...prev, { type: key, keysArr }])
            }
        }
    }, [breakdownObj])

    const extractTypeFromBreakdown = (type) => {
        let keysArr = []
        switch (type) {
            case "digit":
                for (const key in breakdownObj) {
                    if (typeof breakdownObj[key] === "number") keysArr.push({ [key]: breakdownObj[key] })
                }
                break
            case "word":
                for (const key in breakdownObj) {
                    if (typeof breakdownObj[key] === "string" && breakdownObj[key]?.split(" ")?.length <= 1) keysArr.push({ [key]: breakdownObj[key] })
                }
                break
            case "sentence":
                for (const key in breakdownObj) {
                    if (typeof breakdownObj[key] === "string" && breakdownObj[key]?.split(" ")?.length > 1) keysArr.push({ [key]: breakdownObj[key] })
                }
                break
            case "object":
                for (const key in breakdownObj) {
                    if (typeof breakdownObj[key] === "object" && !Array.isArray(breakdownObj[key])) keysArr.push({ [key]: breakdownObj[key] })
                }
                break
            case "array-sentence":
                for (const key in breakdownObj) {
                    if (Array.isArray(breakdownObj[key]) && typeof breakdownObj[key][0] === "string") keysArr.push({ [key]: breakdownObj[key] })
                }
                break
            case "array-object":
                for (const key in breakdownObj) {
                    if (Array.isArray(breakdownObj[key]) && typeof breakdownObj[key][0] === "object") keysArr.push({ [key]: breakdownObj[key] })
                }
                break
            default: keysArr = []
        }
        return keysArr
    }

    const transformKeyText = (text) => text?.split("_")?.join(" ")

    const handleModalClose = () => {
        setShowBreakdown(false)
    }

    if (reportPdfView) return null

    return (
        <>
            <button
                onClick={() => setShowBreakdown(true)}
                className="mx-2 b-20 pl-5 px-3 py-10 view-score-breakdown-btn capitalize text-14 weight-500"
            >
                view details
            </button>
            <Modal
                width={"60vw"}
                open={showBreakdown}
                onModalClose={handleModalClose}
                cancelModal={handleModalClose}
                className="score-breakdown-modal p-20"
                centered
                closeIcon={<CloseIcon />}
                title={<p className="text-30 semibold color-black">Score Breakdown</p>}
            >
                {
                    segregatedBreakdown.length
                        ? <>
                            <div className="grid grid-cols-3 grid-gap-20 my-20">
                                {
                                    segregatedBreakdown.find(category => category?.type === "digit")?.keysArr
                                        ?.map((keyObj, idx) => {
                                            return Object.keys(keyObj)?.map((key, keyIdx) => {
                                                return (
                                                    <div
                                                        key={`${keyIdx}-${idx}`}
                                                        className="flex flex-col items-start"
                                                    >
                                                        <p className="text-19 weight-700 color-black mb-4">{keyObj[key]}</p>
                                                        <p className="text-14 weight-500 color-black capitalize opacity-7">{transformKeyText(key)}</p>
                                                    </div>
                                                )
                                            })
                                        })

                                }
                            </div>
                            <div className="grid grid-cols-3 grid-gap-20 my-20">
                                {
                                    segregatedBreakdown.find(category => category?.type === "word")?.keysArr
                                        ?.map((keyObj, idx) => {
                                            return Object.keys(keyObj)?.map((key, keyIdx) => {
                                                return (
                                                    <div
                                                        key={`${keyIdx}-${idx}`}
                                                        className="flex flex-col items-start"
                                                    >
                                                        <p className="text-19 weight-700 color-black mb-4">{keyObj[key]}</p>
                                                        <p className="text-14 weight-500 color-black capitalize opacity-7">{transformKeyText(key)}</p>
                                                    </div>
                                                )
                                            })
                                        })

                                }
                            </div>
                            <div className="grid grid-cols-1 grid-gap-20 my-20">
                                {
                                    segregatedBreakdown.find(category => (category?.type === "sentence"))?.keysArr
                                        ?.map((keyObj, idx) => {
                                            return Object.keys(keyObj)?.map((key, keyIdx) => {
                                                return (
                                                    <div
                                                        key={`${keyIdx}-${idx}`}
                                                        className="flex flex-col items-start"
                                                    >
                                                        <p className="text-19 weight-700 color-black capitalize mb-4">{transformKeyText(key)}</p>
                                                        <p className="text-14 weight-400 color-black opacity-7">{keyObj[key]}</p>
                                                    </div>
                                                )
                                            })
                                        })
                                }
                            </div>
                            <div className="grid grid-cols-1 grid-gap-20 my-20">
                                {
                                    segregatedBreakdown.find(category => (category?.type === "object"))?.keysArr
                                        ?.map((keyObj, idx) => {
                                            return Object.keys(keyObj)?.map((key, keyIdx) => {
                                                return (
                                                    <div
                                                        key={`${keyIdx}-${idx}`}
                                                        className="grid grid-cols-3 grid-gap-25"
                                                    >
                                                        <p className="text-19 weight-700 color-black capitalize mb-4">{transformKeyText(key)}</p>
                                                        {
                                                            Object.keys(keyObj[key])?.map((objKey, objKeyIdx) => {
                                                                return (
                                                                    <div
                                                                        key={`${keyIdx}-${idx}-${objKeyIdx}`}
                                                                        className="grid grid-cols-3 grid-gap-6"
                                                                    >
                                                                        <p className="text-16 weight-500 color-black capitalize mb-4">{transformKeyText(objKey)}</p>
                                                                        <p className="text-14 weight-400 color-black opacity-7">{keyObj[key][objKey]}</p>
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                )
                                            })
                                        })
                                }
                            </div>
                            <div className="grid grid-cols-1 grid-gap-20 my-20">
                                {
                                    segregatedBreakdown.find(category => (category?.type === "array-sentence"))?.keysArr
                                        ?.map((keyObj, idx) => {
                                            return Object.keys(keyObj)?.map((key, keyIdx) => {
                                                return (
                                                    <div
                                                        key={`${keyIdx}-${idx}`}
                                                        className="flex flex-col items-start"
                                                    >
                                                        <p className="text-19 weight-700 color-black capitalize mb-4">{transformKeyText(key)}</p>
                                                        <ul className="ml-20">
                                                            {
                                                                keyObj[key]?.map((text, textIdx) => (<li
                                                                    key={`${keyIdx}-${idx}-${textIdx}`}
                                                                    className="text-14 weight-400 color-black opacity-7"
                                                                >
                                                                    {text}
                                                                </li>))
                                                            }
                                                        </ul>
                                                    </div>
                                                )
                                            })
                                        })
                                }
                            </div>
                            <div className="grid grid-cols-1 grid-gap-20 my-20">
                                {
                                    segregatedBreakdown.find(category => (category?.type === "array-object"))?.keysArr
                                        ?.map((keyObj, idx) => {
                                            return Object.keys(keyObj)?.map((key, keyIdx) => {
                                                return (
                                                    <div
                                                        key={`${keyIdx}-${idx}`}
                                                        className="flex flex-col items-start"
                                                    >
                                                        <p className="text-19 weight-700 color-black capitalize mb-4">{transformKeyText(key)}</p>
                                                        <div className="grid grid-cols-2 grid-gap-25">
                                                            {
                                                                keyObj[key]?.map((obj, objIdx) => {
                                                                    return (
                                                                        <div
                                                                            key={`${keyIdx}-${idx}-${objIdx}`}
                                                                            className="grid grid-cols-3 grid-gap-4"
                                                                        >
                                                                            {
                                                                                Object.keys(obj)?.map((objKey, objKeyIdx) => {
                                                                                    return (
                                                                                        <div
                                                                                            key={`${keyIdx}-${idx}-${objIdx}-${objKeyIdx}`}
                                                                                            className="flex flex-col items-start"
                                                                                        >
                                                                                            <p className="text-16 weight-500 color-black capitalize mb-4">{obj[objKey]}</p>
                                                                                            <p className="text-14 weight-400 color-black capitalize opacity-7">{transformKeyText(objKey)}</p>
                                                                                        </div>
                                                                                    )
                                                                                })
                                                                            }
                                                                        </div>)
                                                                })
                                                            }
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        })
                                }
                            </div>
                        </>
                        : <></>
                }

            </Modal>
        </>
    )
}

export default ScoreBreakdownModal
